import {
  MailOutline as MailOutlineIcon,
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
} from "@mui/icons-material";

import {
  AppBar,
  Avatar,
  Badge,
  Box,
  IconButton,
  Popover,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { NotificationPopover } from "@/modules/notification/components";

//import { TaasLogo } from "@/shared/branding/TaasLogo";
import { HopeLogo } from "@/shared/branding/TheHopeOnelogo";

import CardViewModeToggle from "@/shared/components/button/CardViewModeToggle";

import { useAuth } from "@/shared/stores/useAuth";

import { useCurrentTalentProfile } from "@/shared/stores/useProfile";

import useThemeStore from "@/shared/stores/useThemeStore";

import {
  NotificationFullData,
  SocketNotificationData,
} from "@/shared/types";

import { isFlutterEmbeddedMobileViewport } from "@/shared/utils/taasEmbeddedContext";

import { useGetNotification } from "../../api/notification.api";

import LanguageSelector from "../LanguageSelector";

import {
  accountMenuPaperSx,
  headerIconButtonSx,
  headerLangButtonSx,
} from "./headerTaasStyles";

import { TalentUserToggleMenu } from "./TalentUserToggleMenu";

/* -------------------------------------------------------------------------- */
/* thehope.one header design tokens                                           */
/* -------------------------------------------------------------------------- */

const hopeHeader = {
  forestDeep: "#111111",

  forest: "#111111",

  forestSoft: "#2a2a2a",

  green: "#5E8F72",

  greenLight: "#83AA91",

  greenSoft: "#DCEADF",

  greenMist: "#EEF5EF",

  ivory: "#FAF8F2",

  cream: "#F3F0E8",

  white: "#FFFFFF",

  text: "#111111",

  muted: "#575757",

  border: "rgba(16,44,36,.10)",

  borderStrong: "rgba(16,44,36,.18)",
};

/* -------------------------------------------------------------------------- */
/* Header Props                                                               */
/* -------------------------------------------------------------------------- */

interface HeaderProps {
  toggleDrawer: () => void;

  setBadgeVisible: (
    visible: boolean
  ) => void;

  badgeVisible: boolean;

  socketNotification: any[];
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export const Header: React.FC<
  HeaderProps
> = ({
  toggleDrawer,
  socketNotification,
  setBadgeVisible,
  badgeVisible,
}) => {
  const [anchorEl, setAnchorEl] =
    useState<null | HTMLElement>(
      null
    );

  const location =
    useLocation();

  const currrentPath =
    location.pathname;

  const { userInfo } =
    useAuth();

  const { currentProfile } =
    useCurrentTalentProfile();

  const theme =
    useTheme();

  const isMobile =
    useMediaQuery(
      theme.breakpoints.down("sm")
    );

  const isFlutterEmbeddedShell =
    isFlutterEmbeddedMobileViewport(
      isMobile
    );

  /* ------------------------------------------------------------------------ */
  /* Notifications                                                            */
  /* ------------------------------------------------------------------------ */

  const {
    data: notificationData,
    refetch,
  } = useGetNotification({
    key: [
      "notification",
      currentProfile?.id,
    ],

    params: {
      offset: 0,

      limit: 10,

      ...(currentProfile?.id && {
        talent_id:
          currentProfile?.id,
      }),
    },

    options: {
      refetchInterval: 30000,
    },
  });

  /* ------------------------------------------------------------------------ */
  /* Coffee call socket state                                                 */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (
      socketNotification.length ===
      0
    ) {
      return;
    }

    /**
     * Process only the most recent
     * coffee-call event.
     *
     * Existing behavior retained.
     */
    const latestCoffeeCallEvent = [
      ...socketNotification,
    ]
      .reverse()
      .find(
        (
          notification: SocketNotificationData
        ) =>
          notification.notification_type ===
            "coffee-call-started" ||
          notification.notification_type ===
            "coffee-call-ended"
      );

    if (
      !latestCoffeeCallEvent
    ) {
      return;
    }

    if (
      latestCoffeeCallEvent.notification_type ===
      "coffee-call-ended"
    ) {
      localStorage.removeItem(
        "coffeeCallNotification"
      );

      return;
    }

    localStorage.setItem(
      "coffeeCallNotification",

      JSON.stringify(
        latestCoffeeCallEvent
      )
    );
  }, [socketNotification]);

  /* ------------------------------------------------------------------------ */
  /* Account menu                                                             */
  /* ------------------------------------------------------------------------ */

  const handleAvatarClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(
      anchorEl
        ? null
        : event.currentTarget
    );
  };

  const open =
    Boolean(anchorEl);

  /**
   * Close account menu when navigating.
   *
   * Existing behavior retained.
   */
  useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  const handleAccountMenuClose =
    () => {
      setAnchorEl(null);
    };

  const navigate =
    useNavigate();

  /* ------------------------------------------------------------------------ */
  /* Notification popover                                                     */
  /* ------------------------------------------------------------------------ */

  const [
    anchorPopover,
    setAnchorPopover,
  ] =
    React.useState<HTMLElement | null>(
      null
    );

  const openPopover =
    Boolean(anchorPopover);

  const headerNotifications: NotificationFullData[] =
    useMemo(() => {
      const list =
        notificationData?.data?.data;

      return Array.isArray(list)
        ? list
        : [];
    }, [
      notificationData?.data?.data,
    ]);

  useEffect(() => {
    if (
      !headerNotifications.length
    ) {
      setBadgeVisible(false);

      return;
    }

    setBadgeVisible(
      headerNotifications.some(
        (notification) =>
          !notification.is_read
      )
    );
  }, [
    headerNotifications,
    setBadgeVisible,
  ]);

  const handlePopoverclick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorPopover(
      event.currentTarget
    );
  };

  const handlePopoverClose =
    () => {
      setAnchorPopover(null);
    };

  const handleMarkedAllRead =
    () => {
      setBadgeVisible(false);

      refetch();
    };

  /* ------------------------------------------------------------------------ */
  /* Theme                                                                    */
  /* ------------------------------------------------------------------------ */

  const {
    darkMode: isDarkMode,
  } = useThemeStore();

  /**
   * Existing headerIconButtonSx behavior is
   * retained, then visually adapted for
   * thehope.one.
   */
  const hopeIconSx = {
    ...headerIconButtonSx,

    width: {
      xs: 34,
      sm: 40,
    },

    height: {
      xs: 34,
      sm: 40,
    },

    borderRadius: {
      xs: "10px",
      sm: "12px",
    },

    color: isDarkMode
      ? "#ffffff"
      : "#111111",

    bgcolor: isDarkMode
      ? "rgba(220,234,223,.04)"
      : "rgba(238,245,239,.45)",

    border: "none",

    boxShadow: "none",

    transition:
      "background-color 180ms ease, color 180ms ease, transform 180ms ease",

    "&:hover": {
      bgcolor: isDarkMode
        ? "rgba(131,170,145,.12)"
        : hopeHeader.greenMist,

      color: isDarkMode
        ? "#ffffff"
        : "#111111",

      boxShadow: "none",
    },

    "&:active": {
      transform: "scale(.96)",
    },
  };

  /* ------------------------------------------------------------------------ */
  /* Account menu control                                                     */
  /* ------------------------------------------------------------------------ */

  const accountMenuControl = (
    <Tooltip
      title="Account menu"
      arrow
    >
      <IconButton
        onClick={
          handleAvatarClick
        }
        sx={{
          padding: {
            xs: 0.5,
            sm: 0.75,
          },

          borderRadius:
            "50%",

          transition:
            "transform 180ms ease",

          "&:hover": {
            bgcolor:
              "transparent",

            transform:
              "translateY(-1px)",
          },
        }}
      >
        <Avatar
          alt="User"
          onContextMenu={(e) =>
            e.preventDefault()
          }
          src={
            currentProfile?.profile_image ??
            userInfo?.profile_image ??
            ""
          }
          sx={{
            width: {
              xs: 32,
              sm: 40,
            },

            height: {
              xs: 32,
              sm: 40,
            },

            bgcolor:
              isDarkMode
                ? "#1a1a1a"
                : "#f4f4f4",

            color:
              isDarkMode
                ? "#ffffff"
                : hopeHeader.forestDeep,

            fontWeight: 700,

            fontFamily:
              "var(--primary-font-family)",

            cursor: "pointer",

            fontSize: {
              xs: 14,
              sm: 16,
            },

            border:
              isDarkMode
                ? "2px solid rgba(131,170,145,.45)"
                : `2px solid ${hopeHeader.greenLight}`,

            boxShadow:
              isDarkMode
                ? "0 0 0 2px rgba(94,143,114,.10)"
                : "0 0 0 3px rgba(94,143,114,.08)",

            transition:
              "border-color 180ms ease, box-shadow 180ms ease",
          }}
        >
          {(
            userInfo
              ?.first_name?.[0] ||
            userInfo
              ?.username?.[0] ||
            "U"
          ).toUpperCase()}
        </Avatar>
      </IconButton>
    </Tooltip>
  );

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) =>
            theme.zIndex.drawer +
            1,

          background:
            isDarkMode
              ? "#000000"
              : "#ffffff",

          backdropFilter:
            "blur(18px)",

          WebkitBackdropFilter:
            "blur(18px)",

          borderBottom:
            isDarkMode
              ? "1px solid rgba(220,234,223,.08)"
              : `1px solid ${hopeHeader.border}`,

          color:
            isDarkMode
              ? "#ffffff"
              : "#111111",

          fontFamily:
            "var(--primary-font-family)",

          boxShadow:
            isDarkMode
              ? "0 8px 30px rgba(0,0,0,.10)"
              : "0 8px 30px rgba(16,44,36,.035)",

          /**
           * Replaces the old gold
           * decorative line.
           */
          "&::after": {
            content: '""',

            position:
              "absolute",

            left: 0,

            right: 0,

            bottom: 0,

            height: "2px",

            pointerEvents:
              "none",

            opacity: 0.8,

            background: isDarkMode
              ? `
                linear-gradient(
                  90deg,
                  transparent 0%,
                  rgba(94,143,114,.18) 12%,
                  rgba(131,170,145,.60) 50%,
                  rgba(120,151,165,.28) 86%,
                  transparent 100%
                )
              `
              : `
                linear-gradient(
                  90deg,
                  transparent 0%,
                  rgba(94,143,114,.10) 10%,
                  rgba(94,143,114,.65) 48%,
                  rgba(120,151,165,.30) 82%,
                  transparent 100%
                )
              `,
          },
        }}
      >
        <Toolbar
          sx={{
            justifyContent:
              "space-between",

            px: {
              xs: 1,
              sm: 2.5,
              md: 4,
            },

            minHeight: {
              xs: 56,
              sm: 72,
            },

            gap: {
              xs: 0.5,
              sm: 1.5,
            },
          }}
        >
          {/* ============================================================ */}
          {/* Left                                                         */}
          {/* ============================================================ */}

          <Box
            sx={{
              display: "flex",

              alignItems:
                "center",

              gap: {
                xs: 0.5,
                sm: 1,
              },

              minWidth: 0,
            }}
          >
            {/*
            Existing drawer control intentionally
            remains commented, just as in the
            original implementation.

            {!isMobile && (
              <Tooltip
                title="Toggle sidebar"
                arrow
              >
                <IconButton
                  onClick={toggleDrawer}
                  sx={hopeIconSx}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
            */}

            <Box
              sx={{
                display:
                  "flex",

                alignItems:
                  "center",

                minWidth: 0,
              }}
            >
              <HopeLogo
                size="sm"
              />
            </Box>
          </Box>

          {/* ============================================================ */}
          {/* Right                                                        */}
          {/* ============================================================ */}

          <Box
            sx={{
              display: "flex",

              alignItems:
                "center",

              gap: {
                xs: 0.5,
                sm: 1,
                md: 1.5,
              },
            }}
          >
            {isFlutterEmbeddedShell ? (
              <>
                {
                  accountMenuControl
                }
              </>
            ) : (
              <React.Fragment>
                {/* ====================================================== */}
                {/* Notifications                                          */}
                {/* ====================================================== */}

                <React.Fragment>
                  <Tooltip
                    title="Notifications"
                    arrow
                  >
                    <IconButton
                      aria-describedby="notifications-popover"
                      aria-expanded={
                        openPopover
                      }
                      onClick={
                        handlePopoverclick
                      }
                      sx={{
                        ...hopeIconSx,

                        ...(openPopover && {
                          bgcolor:
                            isDarkMode
                              ? "rgba(94,143,114,.18)"
                              : hopeHeader.greenSoft,

                          borderColor:
                            isDarkMode
                              ? "rgba(131,170,145,.35)"
                              : "rgba(94,143,114,.32)",

                          color:
                            isDarkMode
                              ? "#ffffff"
                              : hopeHeader.forestDeep,

                          boxShadow:
                            isDarkMode
                              ? "inset 0 0 0 1px rgba(131,170,145,.08)"
                              : "inset 0 0 0 1px rgba(94,143,114,.06)",
                        }),

                        ...(badgeVisible &&
                          !openPopover && {
                            bgcolor:
                              "var(--destructiveAlpha)",

                            borderColor:
                              "var(--destructive-400)",

                            color:
                              "var(--destructive-600)",
                          }),

                        ...(badgeVisible &&
                          openPopover && {
                            bgcolor:
                              isDarkMode
                                ? "rgba(94,143,114,.18)"
                                : hopeHeader.greenSoft,

                            borderColor:
                              isDarkMode
                                ? "rgba(131,170,145,.35)"
                                : "rgba(94,143,114,.32)",

                            color:
                              isDarkMode
                                ? "#ffffff"
                                : hopeHeader.forestDeep,
                          }),
                      }}
                    >
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={
                          !badgeVisible
                        }
                        sx={{
                          "& .MuiBadge-badge":
                            {
                              width: {
                                xs: "8px",
                                sm: "12px",
                              },

                              height: {
                                xs: "8px",
                                sm: "12px",
                              },

                              borderRadius:
                                "50%",

                              backgroundColor:
                                "var(--destructive-600)",

                              border:
                                isDarkMode
                                  ? "2px solid #0D1C17"
                                  : `2px solid ${hopeHeader.ivory}`,

                              boxShadow:
                                badgeVisible
                                  ? "0 0 0 2px var(--destructiveAlpha)"
                                  : "none",

                              animation:
                                badgeVisible &&
                                !openPopover
                                  ? "pulse 2s infinite"
                                  : "none",

                              top: {
                                xs: "6px",
                                sm: "8px",
                              },

                              right: {
                                xs: "6px",
                                sm: "8px",
                              },
                            },

                          "@keyframes pulse":
                            {
                              "0%": {
                                boxShadow:
                                  "0 0 0 0 var(--destructive-500)",
                              },

                              "70%": {
                                boxShadow:
                                  "0 0 0 6px transparent",
                              },

                              "100%": {
                                boxShadow:
                                  "0 0 0 0 transparent",
                              },
                            },
                        }}
                      >
                        <NotificationsIcon
                          sx={{
                            color:
                              openPopover
                                ? isDarkMode
                                  ? "#ffffff"
                                  : hopeHeader.forest
                                : badgeVisible
                                  ? "var(--destructive-600)"
                                  : "inherit",

                            fontSize:
                              {
                                xs: 20,
                                sm: 22,
                              },
                          }}
                        />
                      </Badge>
                    </IconButton>
                  </Tooltip>

                  <NotificationPopover
                    open={
                      openPopover
                    }
                    anchorEl={
                      anchorPopover
                    }
                    onClose={
                      handlePopoverClose
                    }
                    isMobile={
                      isMobile
                    }
                    notifications={
                      headerNotifications
                    }
                    onMarkedAllRead={
                      handleMarkedAllRead
                    }
                  />
                </React.Fragment>

                {/* ====================================================== */}
                {/* Desktop Language Selector                              */}
                {/* ====================================================== */}

                <Box
                  sx={{
                    display: {
                      xs: "none",
                      sm: "flex",
                    },

                    alignItems:
                      "center",

                    mr: 1,
                  }}
                >
                  <Box
                    sx={{
                      "& .MuiButton-root":
                        {
                          ...headerLangButtonSx,

                          height: 36,

                          px: 1.5,

                          fontSize: 12,

                          borderRadius:
                            "10px",

                          color:
                            isDarkMode
                              ? "#ffffff"
                              : hopeHeader.forestDeep,

                          bgcolor:
                            isDarkMode
                              ? "rgba(220,234,223,.035)"
                              : "rgba(238,245,239,.45)",

                          border: "none",

                          boxShadow:
                            "none",

                          "&:hover":
                            {
                              bgcolor:
                                isDarkMode
                                  ? "rgba(94,143,114,.12)"
                                  : hopeHeader.greenMist,

                              color:
                                isDarkMode
                                  ? "#ffffff"
                                  : hopeHeader.forest,
                            },
                        },
                    }}
                  >
                    <LanguageSelector variant="button" />
                  </Box>
                </Box>

                {/* ====================================================== */}
                {/* Mobile Language Selector                               */}
                {/* ====================================================== */}

                <Box
                  sx={{
                    display: {
                      xs: "flex",
                      sm: "none",
                    },

                    alignItems:
                      "center",

                    mr: 0,
                  }}
                >
                  <Box
                    sx={{
                      "& .MuiButton-root":
                        {
                          ...headerLangButtonSx,

                          height: 28,

                          px: 0.75,

                          fontSize: 9,

                          borderRadius:
                            "8px",

                          fontWeight: 700,

                          letterSpacing:
                            "0.5px",

                          color:
                            isDarkMode
                              ? "#ffffff"
                              : hopeHeader.forestDeep,

                          bgcolor:
                            "transparent",

                          border: "none",

                          boxShadow:
                            "none",

                          "&:hover":
                            {
                              bgcolor:
                                isDarkMode
                                  ? "rgba(94,143,114,.10)"
                                  : hopeHeader.greenMist,
                            },
                        },
                    }}
                  >
                    <LanguageSelector
                      variant="button"
                      compact={true}
                    />
                  </Box>
                </Box>

                {/* ====================================================== */}
                {/* Account                                                */}
                {/* ====================================================== */}

                {
                  accountMenuControl
                }
              </React.Fragment>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ================================================================== */}
      {/* Avatar Dropdown                                                    */}
      {/* ================================================================== */}

      <Popover
        onContextMenu={(e) =>
          e.preventDefault()
        }
        open={open}
        anchorEl={anchorEl}
        onClose={
          handleAccountMenuClose
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              ...accountMenuPaperSx,

              p: 0,

              mt: 1,

              borderRadius:
                "14px",

              overflow:
                "hidden",

              bgcolor:
                isDarkMode
                  ? "#000000"
                  : "#FFFFFF",

              color:
                isDarkMode
                  ? "#ffffff"
                  : "#111111",

              border: "none",

              boxShadow:
                isDarkMode
                  ? "0 20px 50px rgba(0,0,0,.32)"
                  : "0 20px 50px rgba(0,0,0,.10)",

              backgroundImage:
                "none",
            },
          },
        }}
      >
        {open && (
          <TalentUserToggleMenu
            talentListClose={
              handleAccountMenuClose
            }
          />
        )}
      </Popover>
    </>
  );
};